import { motion } from "framer-motion";
import { Trees, Bath, Tent, Clock, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./motion";

const OLIVE = "#3f4a33";
const GOLD = "#b4924e";
const LABEL = "#4a4636";
const FAINT = "#8a8468";

/* Lower South Island traced from the owner's original artwork:
   sea in the top-left (Tasman), the island running diagonally and bleeding
   off the top-right corner (Marlborough), Banks Peninsula bump at Christchurch,
   Fiordland notches bottom-left, Pacific at bottom-right (compass in the sea). */
const LAND =
  "M490 0 C420 60 340 120 250 190 C160 260 80 300 0 345 C25 405 10 445 35 485 C60 525 28 565 48 605 C68 645 40 692 82 732 C122 772 180 812 260 830 C340 845 420 840 460 838 C540 830 612 800 662 748 C722 688 752 620 770 542 C785 472 780 420 790 370 C830 350 862 330 858 300 C852 275 820 268 795 258 C820 210 855 175 880 150 L880 0 Z";

const RIDGES = [
  "M180 300 C240 340 300 380 380 420",
  "M300 220 C380 280 460 340 540 400",
  "M420 520 C480 560 540 600 600 660",
  "M120 560 C180 600 240 640 300 690",
  "M500 180 C560 230 620 280 690 330",
];

const FIORDS = [
  "M42 610 C30 616 28 624 40 630",
  "M52 648 C38 656 36 664 50 670",
  "M78 690 C64 698 62 706 76 712",
];

function Route({ d, delay }) {
  return (
    <g fill="none">
      <motion.path
        d={d}
        stroke={GOLD}
        strokeWidth={2}
        strokeLinecap="round"
        opacity={0.4}
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1.6, delay, ease: [0.16, 1, 0.3, 1] }}
      />
      <motion.path
        d={d}
        stroke={GOLD}
        strokeWidth={2.6}
        strokeLinecap="round"
        strokeDasharray="0.5 8"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.9, delay: delay + 1.2 }}
      />
    </g>
  );
}

function Tree({ x, y, s = 1 }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`} opacity={0.55}>
      <path d="M0 0 L-5 10 L-2 10 L-2 16 L2 16 L2 10 L5 10 Z" fill="#77755a" />
    </g>
  );
}

function CarGlyph({ x, y }) {
  return (
    <g transform={`translate(${x} ${y})`} fill="#6b6450">
      <path d="M0 5.5 L2 2.5 L11.5 2.5 L13.5 5.5 L14.5 5.5 L14.5 8 L0 8 Z" />
      <circle cx="3.6" cy="8.6" r="1.7" />
      <circle cx="11" cy="8.6" r="1.7" />
    </g>
  );
}

function TimePill({ x, y, w, label, delay }) {
  return (
    <motion.g
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, delay }}
    >
      <rect x={x} y={y} width={w} height={26} rx={13} fill="#f1e9d8" stroke="#c9b98f" strokeWidth={1} />
      <CarGlyph x={x + 12} y={y + 6} />
      <text className="map-pill-text" x={x + 30} y={y + 17.5} fontSize={12} letterSpacing={1.2} fill="#6b6450" fontFamily="Manrope, sans-serif">
        {label}
      </text>
    </motion.g>
  );
}

function StopoverMap() {
  return (
    <div
      className="rounded-[28px] border border-white/10 bg-[#f5f0e7] p-3 md:p-4 shadow-2xl shadow-black/50"
      data-testid="stopover-map-plate"
    >
      <svg viewBox="0 0 880 840" className="w-full h-auto rounded-[20px]" role="img" aria-label="Illustrated map of the South Island showing Danseys Pass Lavender Farm between Christchurch, Wānaka and Queenstown">
        <defs>
          <filter id="softBlur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="10" />
          </filter>
          <filter id="paperGrain">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
            <feComponentTransfer><feFuncA type="linear" slope="0.05" /></feComponentTransfer>
          </filter>
        </defs>

        <rect x="0" y="0" width="880" height="840" rx="20" fill="#f5f0e7" />
        <rect x="14" y="14" width="852" height="812" rx="14" fill="none" stroke="#d8c9ac" strokeWidth="1.5" />

        {/* land */}
        <path d={LAND} fill="#d9c8a8" opacity={0.55} filter="url(#softBlur)" />
        <path d={LAND} fill="#eae0cc" />
        <path d={LAND} fill="none" stroke="#cbb891" strokeWidth={2} opacity={0.7} />
        <g opacity={0.3} fill="#dccfae" filter="url(#softBlur)">
          <ellipse cx="360" cy="260" rx="130" ry="90" />
          <ellipse cx="460" cy="560" rx="150" ry="105" />
          <ellipse cx="210" cy="640" rx="90" ry="70" />
        </g>

        {/* mountain ridge texture */}
        <g fill="none" stroke="#d3c6a4" strokeWidth={2.5} strokeLinecap="round" opacity={0.5}>
          {RIDGES.map((d) => <path key={d} d={d} />)}
        </g>

        {/* fiords on the west coast */}
        <g fill="none" stroke="#b9c6c8" strokeWidth={3} strokeLinecap="round" opacity={0.8}>
          {FIORDS.map((d) => <path key={d} d={d} />)}
        </g>

        {/* lakes */}
        <g fill="#c8d2d2" opacity={0.6}>
          <ellipse cx="252" cy="486" rx="12" ry="34" transform="rotate(24 252 486)" />
          <ellipse cx="290" cy="516" rx="10" ry="30" transform="rotate(30 290 516)" />
          <ellipse cx="176" cy="660" rx="26" ry="46" transform="rotate(18 176 660)" />
        </g>

        {/* trees */}
        <Tree x={420} y={90} s={0.85} />
        <Tree x={560} y={150} s={0.9} />
        <Tree x={680} y={420} />
        <Tree x={600} y={690} s={1.1} />
        <Tree x={120} y={760} />

        {/* routes */}
        <Route d="M760 250 C700 290 636 306 596 340 C556 374 508 398 452 418" delay={0.3} />
        <Route d="M198 411 C262 438 330 420 380 430 C400 434 418 431 436 428" delay={0.55} />
        <Route d="M147 576 C212 560 262 558 312 538 C362 518 402 468 438 434" delay={0.8} />

        {/* destination dots + labels */}
        <g fontFamily="Manrope, sans-serif">
          <motion.g initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <circle cx="760" cy="250" r={7} fill={OLIVE} />
            <circle cx="760" cy="250" r={2.6} fill="#f5f0e7" />
            <text className="map-label" x="760" y="290" fontSize={15} letterSpacing={3.2} fill={LABEL} textAnchor="middle">CHRISTCHURCH</text>
            <TimePill x={698} y={302} w={124} label="approx. 4.5 HRS" delay={0.7} />
          </motion.g>

          <motion.g initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: 0.35 }}>
            <text className="map-label" x="200" y="386" fontSize={15} letterSpacing={3.2} fill={LABEL} textAnchor="middle">WĀNAKA</text>
            <circle cx="198" cy="411" r={7} fill={OLIVE} />
            <circle cx="198" cy="411" r={2.6} fill="#f5f0e7" />
            <TimePill x={138} y={440} w={140} label="approx. 2.5–3 HRS" delay={0.85} />
          </motion.g>

          <motion.g initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: 0.5 }}>
            <circle cx="147" cy="576" r={7} fill={OLIVE} />
            <circle cx="147" cy="576" r={2.6} fill="#f5f0e7" />
            <text className="map-label" x="146" y="616" fontSize={15} letterSpacing={3.2} fill={LABEL} textAnchor="middle">QUEENSTOWN</text>
            <TimePill x={80} y={628} w={140} label="approx. 3.5–4 HRS" delay={1} />
          </motion.g>
        </g>

        {/* farm pin */}
        <motion.g
          initial={{ opacity: 0, y: -14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 1.35 }}
        >
          <ellipse cx="427" cy="462" rx="20" ry="6" fill="#3f4a33" opacity={0.14} />
          <path d="M427 460 C409 434 401 421 401 406 a26 26 0 1 1 52 0 c0 15 -8 28 -26 54 z" fill={OLIVE} />
          <circle cx="427" cy="405" r={15.5} fill="#f5f0e7" />
          <g stroke="#8c7a8f" strokeWidth={1.6} strokeLinecap="round" fill="none">
            <path d="M427 413 L427 398" />
            <path d="M427 404 C423 401 421 397 421 393" />
            <path d="M427 404 C431 401 433 397 433 393" />
            <path d="M427 409 C424 407 423 404 423 401" />
            <path d="M427 409 C430 407 431 404 431 401" />
          </g>
          <text className="map-farmname" x="427" y="502" fontSize={21} letterSpacing={4.2} fill={LABEL} textAnchor="middle" fontFamily="Manrope, sans-serif">DANSEYS PASS</text>
          <text className="map-farmsub" x="427" y="527" fontSize={12} letterSpacing={5} fill={FAINT} textAnchor="middle" fontFamily="Manrope, sans-serif">LAVENDER FARM</text>
        </motion.g>

        {/* compass — sits in the Pacific, bottom right */}
        <motion.g
          transform="translate(783 726)"
          initial={{ opacity: 0, rotate: -30 }}
          whileInView={{ opacity: 1, rotate: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, delay: 1.2 }}
        >
          <circle r="26" fill="none" stroke={GOLD} strokeWidth="1.4" opacity={0.9} />
          <path d="M0 -18 L4.5 0 L0 18 L-4.5 0 Z" fill={GOLD} />
          <path d="M-18 0 L0 4 L18 0 L0 -4 Z" fill="#cbb891" />
          <text y="-36" textAnchor="middle" fontSize={14} fill={LABEL} fontFamily="Manrope, sans-serif">N</text>
        </motion.g>

        <text className="map-caption" x="36" y="806" fontSize={11} letterSpacing={4} fill="#9a9070" fontFamily="Manrope, sans-serif">
          SOUTH ISLAND · AOTEAROA NEW ZEALAND
        </text>

        <rect x="0" y="0" width="880" height="840" rx="20" filter="url(#paperGrain)" opacity={0.5} />
      </svg>
    </div>
  );
}

const FEATURES = [
  { icon: Trees, label: "Open space", testid: "stopover-feature-openspace" },
  { icon: Bath, label: "Private hot tubs", testid: "stopover-feature-hottubs" },
  { icon: Tent, label: "Rustic comfort", testid: "stopover-feature-comfort" },
  { icon: Clock, label: "A slower pace", testid: "stopover-feature-pace" },
];

const DRIVE_TIMES = [
  ["Christchurch", "4.5 hrs"],
  ["Wānaka", "2.5–3 hrs"],
  ["Queenstown", "3.5–4 hrs"],
];

export default function PerfectStopover() {
  return (
    <section className="py-24 md:py-32 px-6 md:px-16 bg-moss" data-testid="home-stopover-section">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow className="mb-6">Getting here</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 className="display-2 text-cream">
              The <span className="serif-italic text-lavglow">perfect stopover.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-sage mt-8 leading-relaxed text-sm md:text-base max-w-md">
              Set between Christchurch, Wānaka and Queenstown, Danseys Pass is an easy
              place to break the journey — with open space, rustic comfort, private hot
              tubs and a slower pace waiting when you arrive.
            </p>
          </Reveal>
          <div className="mt-8 lg:hidden" data-testid="stopover-drive-times">
            {DRIVE_TIMES.map(([city, time], i) => (
              <Reveal key={city} delay={0.2 + i * 0.06} y={14}>
                <div className="flex items-center justify-between border border-white/10 rounded-xl px-4 py-3 mb-2">
                  <span className="font-mono text-[0.62rem] uppercase tracking-widest2 text-sage">{city}</span>
                  <span className="font-mono text-[0.62rem] uppercase tracking-widest2 text-lavglow">approx. {time}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 max-w-md">
            {FEATURES.map((f, i) => (
              <Reveal key={f.label} delay={0.2 + i * 0.08} testid={f.testid}>
                <div className="flex items-center gap-3 border border-white/10 rounded-2xl px-4 py-4 hover:border-lavglow/40 transition-colors">
                  <f.icon size={18} className="text-lavglow shrink-0" />
                  <span className="font-mono text-[0.6rem] uppercase tracking-widest2 text-sage leading-snug">
                    {f.label}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.4}>
            <p className="font-mono text-[0.56rem] uppercase tracking-widest2 text-fern mt-8 mb-3">En route &amp; nearby</p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md">
              {[
                ["Danseys Pass Hotel", "https://www.danseyspass.co.nz/", "stopover-link-hotel"],
                ["Oteake Conservation Park", "https://www.doc.govt.nz/parks-and-recreation/places-to-go/otago/places/oteake-conservation-park/", "stopover-link-oteake"],
              ].map(([label, url, tid]) => (
                <a
                  key={tid}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-white/10 rounded-full px-4 py-2.5 text-xs text-sage hover:text-lavglow hover:border-lavglow/40 transition-colors"
                  data-testid={tid}
                >
                  {label}
                  <ArrowUpRight size={13} className="shrink-0" />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.15} y={40}>
            <StopoverMap />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
